/**
 * 手动赠课记录明细区（只读，entitlement-core:SC-35 账本下钻）。
 *
 * 按记录 ID 调 GET /manual-trial-grants/:id 展示次数/原因/操作人/时间与
 * 学生、SKU、关联账户摘要。两处消费：账本流水详情抽屉内嵌区
 * （LedgerDetailDrawer）、全机构流水页业务编号下钻抽屉（LedgerPage，
 * MANUAL_GRANT 来源，SC-15 交互模式）。记录不存在（404）经 onNotFound
 * 上抛，由调用方切换回退说明抽屉（SC-16 交互模式）。
 */
import { useEffect, useRef, useState } from 'react';
import { Descriptions, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  manualTrialGrantApi,
  type ManualTrialGrantDetail,
} from '../../api/manual-trial-grant.api';
import { ApiError } from '../../api/transport';

const { Text } = Typography;

function formatDateTime(value: string): string {
  return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
}

/** 按记录 ID 加载赠课明细（对齐 useEntryDetailWhen 模式：onNotFound 经 ref 转发不重触发）。 */
export function useManualTrialGrantDetail(
  recordId: string | null,
  onNotFound?: (recordId: string) => void,
): { detail: ManualTrialGrantDetail | null; loading: boolean; failed: boolean } {
  const [detail, setDetail] = useState<ManualTrialGrantDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // onNotFound 经 ref 转发，调用方可传内联函数而不重触发加载
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;

  useEffect(() => {
    if (!recordId) {
      setDetail(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void manualTrialGrantApi
      .getManualTrialGrantDetail(recordId)
      .then((r) => {
        if (!cancelled) setDetail(r);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setFailed(true);
        if (err instanceof ApiError && err.status === 404) {
          notFoundRef.current?.(recordId);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  return { detail, loading, failed };
}

/** 赠课记录明细只读展示（加载中/失败态为内联文案）。 */
export function ManualTrialGrantDetailSection({
  detail,
  loading,
  failed,
}: {
  detail: ManualTrialGrantDetail | null;
  loading: boolean;
  failed: boolean;
}) {
  if (loading) {
    return (
      <Text type="secondary" data-testid="manual-grant-detail-loading">
        赠课记录明细加载中…
      </Text>
    );
  }
  if (failed) {
    return (
      <Text type="warning" data-testid="manual-grant-detail-failed">
        赠课记录明细加载失败或不存在
      </Text>
    );
  }
  if (!detail) return null;
  return (
    <Descriptions column={1} size="small" bordered data-testid="manual-grant-record-detail">
      <Descriptions.Item label="记录编号">{detail.id}</Descriptions.Item>
      <Descriptions.Item label="赠课次数">{detail.quantity} 次</Descriptions.Item>
      <Descriptions.Item label="赠课原因">{detail.reason}</Descriptions.Item>
      <Descriptions.Item label="操作人">{detail.operatorName ?? '—'}</Descriptions.Item>
      <Descriptions.Item label="赠课时间">{formatDateTime(detail.createdAt)}</Descriptions.Item>
      <Descriptions.Item label="学生">{detail.studentName}</Descriptions.Item>
      <Descriptions.Item label="课程 SKU">{detail.skuName}</Descriptions.Item>
      <Descriptions.Item label="关联试课账户">{detail.accountId ?? '—'}</Descriptions.Item>
    </Descriptions>
  );
}
